import React from "react";
import PropTypes from "prop-types";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";

/**
 * Returns the page numbers to display, with "..." for skipped ranges. Always shows the
 * first and last page and `siblings` pages either side of the current page. A gap of a
 * single page is shown as that page instead of "...".
 *   e.g. (page 7 of 20, siblings 1) => [1, "...", 6, 7, 8, "...", 20]
 */
export const getPageItems = (currPage, pageCount, siblings = 1) => {
  const pages = new Set([1, pageCount]);
  for (let p = currPage - siblings; p <= currPage + siblings; p++)
    if (p >= 1 && p <= pageCount) pages.add(p);

  const sorted = [...pages].sort((a, b) => a - b);
  const items = [];
  sorted.forEach((p, i) => {
    const prev = sorted[i - 1];
    if (prev !== undefined && p - prev === 2) items.push(p - 1);
    else if (prev !== undefined && p - prev > 2) items.push("...");
    items.push(p);
  });
  return items;
};

/**
 * Bootstrap pagination bar: Previous, page numbers, Next.
 * Renders nothing when there is only one page.
 */
const PageNav = ({ currPage, pageCount, onPageChange, disabled, siblings, className, ariaLabel }) => {
  if (!pageCount || pageCount <= 1) return null;

  const goTo = (page) => (e) => {
    e.preventDefault();
    if (!disabled && page !== currPage && page >= 1 && page <= pageCount) onPageChange(page);
  };

  return (
    <Pagination className={className} listClassName="mb-0" aria-label={ariaLabel}>
      <PaginationItem disabled={disabled || currPage <= 1}>
        <PaginationLink href="#" onClick={goTo(currPage - 1)}>
          Previous
        </PaginationLink>
      </PaginationItem>
      {getPageItems(currPage, pageCount, siblings).map((item, i) =>
        item === "..." ? (
          <PaginationItem key={`ellipsis_${i}`} disabled>
            <PaginationLink href="#" onClick={(e) => e.preventDefault()}>
              &hellip;
            </PaginationLink>
          </PaginationItem>
        ) : (
          <PaginationItem key={item} active={item === currPage} disabled={disabled && item !== currPage}>
            <PaginationLink
              href="#"
              onClick={goTo(item)}
              aria-current={item === currPage ? "page" : undefined}
            >
              {item}
            </PaginationLink>
          </PaginationItem>
        )
      )}
      <PaginationItem disabled={disabled || currPage >= pageCount}>
        <PaginationLink href="#" onClick={goTo(currPage + 1)}>
          Next
        </PaginationLink>
      </PaginationItem>
    </Pagination>
  );
};

PageNav.propTypes = {
  currPage: PropTypes.number.isRequired, // 1-based
  pageCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired, // receives the new 1-based page number
  disabled: PropTypes.bool, // e.g. while a page is loading
  siblings: PropTypes.number, // pages shown either side of the current page
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

PageNav.defaultProps = {
  disabled: false,
  siblings: 1,
  className: "",
  ariaLabel: "Page navigation",
};

export default PageNav;
